import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  BudgetCategory,
  SpendingTransaction,
} from "../../lib/api-modules/types.js";
import { formatCentsCompact } from "../../lib/util/money.js";

/**
 * <BudgetVsActualChart> — month-level budget vs. actual spending,
 * one bar group per category. Shows uncategorized spending as a
 * separate "Uncategorized" bar so users notice it as something to
 * triage.
 *
 * Computed client-side via a O(C * T) join (C categories × T tx).
 * For Hearth's typical monthly volume (~10 categories, ~200 tx)
 * this is trivially fast.
 */

interface BudgetVsActualChartProps {
  readonly categories: readonly BudgetCategory[];
  readonly transactions: readonly SpendingTransaction[];
}

interface ChartRow {
  readonly name: string;
  readonly budget_cents: number;
  readonly actual_cents: number;
}

const UNCATEGORIZED = "Uncategorized";

export function BudgetVsActualChart({
  categories,
  transactions,
}: BudgetVsActualChartProps) {
  const data = buildChartData(categories, transactions);

  if (data.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400"
        data-testid="budget-vs-actual-empty"
      >
        Add categories with monthly budgets to see budget-vs-actual.
      </div>
    );
  }

  return (
    <div className="h-[300px] w-full" data-testid="budget-vs-actual-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <BarChart
          data={data}
          margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
        >
          <CartesianGrid strokeOpacity={0.1} vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            interval={0}
            angle={-30}
            textAnchor="end"
            height={64}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            tickFormatter={(v: number) => formatCentsCompact(v)}
            width={64}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null;
              const budget = payload.find((p) => p.dataKey === "budget_cents")
                ?.value as number | undefined;
              const actual = payload.find((p) => p.dataKey === "actual_cents")
                ?.value as number | undefined;
              return (
                <div className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-xs shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                  <div className="font-medium">{label}</div>
                  {typeof budget === "number" && (
                    <div className="tabular-nums text-zinc-600 dark:text-zinc-400">
                      Budget: {formatCentsCompact(budget)}
                    </div>
                  )}
                  {typeof actual === "number" && (
                    <div
                      className={`tabular-nums ${
                        typeof budget === "number" && actual > budget
                          ? "text-red-600 dark:text-red-400"
                          : "text-emerald-600 dark:text-emerald-400"
                      }`}
                    >
                      Actual: {formatCentsCompact(actual)}
                    </div>
                  )}
                </div>
              );
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            formatter={(v: string) =>
              v === "budget_cents" ? "Budget" : "Actual"
            }
          />
          <Bar
            dataKey="budget_cents"
            fill="rgb(168 162 158)"
            isAnimationActive={false}
          />
          <Bar
            dataKey="actual_cents"
            fill="rgb(16 185 129)"
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function buildChartData(
  categories: readonly BudgetCategory[],
  transactions: readonly SpendingTransaction[],
): ChartRow[] {
  // Sum tx by category_id. Uncategorized rows aggregate under the
  // sentinel UNCATEGORIZED bucket.
  const byCategory = new Map<string, number>();
  for (const t of transactions) {
    const key = t.category_id ?? UNCATEGORIZED;
    byCategory.set(key, (byCategory.get(key) ?? 0) + t.amount_cents);
  }
  const rows: ChartRow[] = [];
  for (const c of categories) {
    if (c.is_active === false) continue;
    rows.push({
      name: c.name,
      budget_cents: c.monthly_budget_cents ?? 0,
      actual_cents: byCategory.get(c.category_id) ?? 0,
    });
    byCategory.delete(c.category_id);
  }
  // Anything remaining is uncategorized or pointing at an inactive
  // category. Surface uncategorized explicitly.
  const uncategorizedActual = byCategory.get(UNCATEGORIZED) ?? 0;
  if (uncategorizedActual > 0) {
    rows.push({
      name: UNCATEGORIZED,
      budget_cents: 0,
      actual_cents: uncategorizedActual,
    });
  }
  return rows;
}
